import { withErrorBoundary } from '@sentry/react';
import React from 'react';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainerM2030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import DataRow from '../../components/data-viewers/data-row/data-row';
import { Fallback } from '../../components/utils/error-boundary';
import { formatDateTime } from '../../helpers/date-formatters';
import { formatNumber } from '../../helpers/number-formatters';
import { StatusIcon, stringifyStatus } from '../../helpers/type-formatters';
import { GraphqlOperations } from '../../logic/graphql/operations';
import {
  Query,
  SecurityTransferListItem,
  Status,
  Subscription,
} from '../../logic/graphql/types/types.d';
import { Paths } from '../../navigation/paths';
import { FormattedMessage, useIntl } from 'react-intl';

const SecurityTransferDetails = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch();
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const id: string = (match.params as any).id || '';

  const [securityTransferQueryResult, reexecuteSecurityTransferQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_security_transfer.queryString,
    variables: GraphqlOperations.queries.get_security_transfer.createVars(id),
    pause: id === '',
    requestPolicy: 'network-only',
  });
  const securityTransfer = securityTransferQueryResult.data?.get_security_transfer;

  const handleUpdateSecurityTransferSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionSecurityTransfers: SecurityTransferListItem[] = [],
    response: Subscription,
  ): SecurityTransferListItem[] => {
    if (response && response.update_security_transfer) {
      if (
        response.update_security_transfer.transaction_code ===
        securityTransferQueryResult.data?.get_security_transfer.transaction_code
      ) {
        reexecuteSecurityTransferQuery();
      }
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_security_transfer.subscriptionString,
    },
    handleUpdateSecurityTransferSubscription,
  );

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleCancelClick = (): void => {
    history.push(Paths.contact);
  };

  if (id === '') {
    return <Redirect to={Paths.security_transfers} />;
  }

  const title = securityTransfer?.security.name
    ? securityTransfer?.security.name +
      intl.formatMessage({ id: 'securityTransfers.details.titleSecurityExtension' })
    : intl.formatMessage({ id: 'securityTransfers.details.defaultTitle' });
  const titleWithTransactionCode = securityTransfer?.transaction_code
    ? title + ' - ' + securityTransfer.transaction_code
    : title;

  return (
    <DefaultScreen
      background={assets.images.orders_bg}
      title={titleWithTransactionCode}
      onBackClick={handleBackClick}
      loading={securityTransferQueryResult.fetching}
      rightContent={
        <aside className="flex flex-row items-center">
          <StatusIcon status={securityTransfer?.status} />
          <h2 className="ml-2">{stringifyStatus(securityTransfer?.status, intl)}</h2>
        </aside>
      }
    >
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 lg:grid-cols-2 mb-6">
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id="securityTransfers.details.dataLabel" />
          </h2>
          <DataRow
            label={intl.formatMessage({ id: 'securityTransfers.details.fromAccountLabel' })}
            value={securityTransfer?.account}
          />
          <DataRow
            label={intl.formatMessage({ id: 'securityTransfers.details.toAccountLabel' })}
            value={securityTransfer?.to_provider}
          />
          <DataRow
            label={intl.formatMessage({ id: 'securityTransfers.details.countLabel' })}
            value={formatNumber(
              securityTransfer?.amount,
              intl.formatMessage({ id: 'securityTransfers.details.pcs' }),
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'securityTransfers.details.transferDateLabel' })}
            value={formatDateTime(securityTransfer?.order_date)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'securityTransfers.details.fulfillmentDateLabel' })}
            value={formatDateTime(securityTransfer?.fulfillment_date)}
          />
        </BlurredContainerM2030>
      </div>
      {securityTransfer?.status === Status.Active ? (
        <Button secondary onClick={handleCancelClick}>
          <FormattedMessage id="securityTransfers.details.cancelButton" />
        </Button>
      ) : null}
    </DefaultScreen>
  );
};

export default withErrorBoundary(SecurityTransferDetails, { fallback: Fallback });
